import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { useUser } from '../context/UserContext';
import { findProblemById } from '../data/problems';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Tab = 'friends' | 'requests' | 'activity' | 'find';

interface FriendRow {
  user_id: string;
  handle: string;
  accepted_at?: string;
  requested_at?: string;
}

interface ActivityEvent {
  user_id: string;
  handle: string;
  problem_id: string;
  solved_at: string;     // ISO 8601
  xp_earned: number;
  topic: string;
}

// "5m ago" / "2h ago" / "3d ago" — small relative-time helper so the feed
// has the same texture as a social timeline without pulling in date-fns.
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toISOString().slice(0, 10);
}

const FriendsPage: React.FC = () => {
  useDocumentTitle('friends');
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { refresh: refreshUser } = useUser();
  const idToken = authState.kind === 'authenticated' ? authState.idToken : undefined;

  const [tab, setTab] = useState<Tab>('friends');
  const [friends, setFriends] = useState<FriendRow[]>([]);
  const [incoming, setIncoming] = useState<FriendRow[]>([]);
  const [outgoing, setOutgoing] = useState<FriendRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Friend-activity-feed state — fetched lazily when the activity tab opens.
  const [activity, setActivity] = useState<ActivityEvent[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  // Find-tab state.
  const [findHandle, setFindHandle] = useState('');
  const [finding, setFinding] = useState(false);
  const [findMsg, setFindMsg] = useState('');

  const loadFriends = useCallback(async () => {
    if (!idToken) return;
    setLoading(true); setError('');
    try {
      const [fRes, rRes] = await Promise.all([
        api('/api/friends', { token: idToken }),
        api('/api/friends/requests', { token: idToken }),
      ]);
      if (!fRes.ok || !rRes.ok) {
        setError(`load failed (${fRes.status}/${rRes.status})`);
        return;
      }
      const fData = await fRes.json();
      const rData = await rRes.json();
      setFriends(fData.friends ?? []);
      setIncoming(rData.incoming ?? []);
      setOutgoing(rData.outgoing ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => { void loadFriends(); }, [loadFriends]);

  // Lazy-load the activity feed when the user first opens the tab so /friends
  // doesn't pay the fan-out cost on cold mount.
  const loadActivity = useCallback(async () => {
    if (!idToken) return;
    setActivityLoading(true);
    try {
      const res = await api('/api/friends/activity', { token: idToken });
      if (!res.ok) {
        setError(`activity load failed (${res.status})`);
        setActivity([]);
        return;
      }
      const data = await res.json();
      setActivity(data.events ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'activity load failed');
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (tab === 'activity' && activity === null) {
      void loadActivity();
    }
  }, [tab, activity, loadActivity]);

  const sendRequest = async (target: string) => {
    if (!idToken) return;
    setFinding(true); setFindMsg('');
    try {
      const res = await api('/api/friends/request', {
        method: 'POST', token: idToken, body: { target_handle: target },
      });
      if (res.status === 404) { setFindMsg('no user with that handle'); return; }
      if (res.status === 409) { setFindMsg('already friends'); return; }
      if (res.status === 400) { setFindMsg('that handle isn\'t valid'); return; }
      if (res.status === 429) { setFindMsg('slow down — too many requests'); return; }
      if (!res.ok) { setFindMsg(`failed (${res.status})`); return; }
      const data = await res.json();
      setFindMsg(data.status === 'accepted' ? 'now friends!' : 'request sent');
      setFindHandle('');
      void loadFriends();
      void refreshUser();
    } catch (e) {
      setFindMsg(e instanceof Error ? e.message : 'failed');
    } finally {
      setFinding(false);
    }
  };

  const accept = async (other_id: string) => {
    if (!idToken) return;
    setError('');
    const res = await api(`/api/friends/${other_id}/accept`, { method: 'POST', token: idToken });
    if (!res.ok) {
      // 409 happens if the row flipped to accepted between list refresh and
      // click (other side accepted concurrently, or someone double-clicked).
      // Treat as harmless — reload reconciles the UI.
      if (res.status === 409) {
        void loadFriends();
        return;
      }
      const text =
        res.status === 429 ? 'slow down — too many accepts'
        : res.status === 404 ? 'that user no longer exists'
        : `accept failed (${res.status})`;
      setError(text);
      return;
    }
    void loadFriends();
    void refreshUser();
  };

  const remove = async (other_id: string) => {
    if (!idToken) return;
    setError('');
    const res = await api(`/api/friends/${other_id}`, { method: 'DELETE', token: idToken });
    if (!res.ok) {
      const text =
        res.status === 429 ? 'slow down — too many removes'
        : res.status === 404 ? 'that user no longer exists'
        : `remove failed (${res.status})`;
      setError(text);
      return;
    }
    void loadFriends();
    void refreshUser();
  };

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'friends',  label: 'friends',  count: friends.length },
    { id: 'requests', label: 'requests', count: incoming.length + outgoing.length },
    { id: 'activity', label: 'activity' },
    { id: 'find',     label: 'find' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--paper-2)' }}>
      <div style={{ padding: '12px 18px', borderBottom: '1.4px dashed var(--ink)', background: 'var(--paper)', flexShrink: 0 }}>
        <div className="mono-cap">
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/')}>map</span>
          {' › '}friends
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>friends</h1>
        <div style={{ display: 'flex', gap: 5, marginTop: 12 }}>
          {tabs.map(t => (
            <button
              key={t.id}
              className={`sk-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
              style={{ fontFamily: 'inherit', cursor: 'pointer' }}
            >
              {t.label}{t.count !== undefined ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="scroll-y" style={{ flex: 1, padding: 12 }}>
        {error && <div style={{ fontSize: 11, color: 'var(--w-coral)', marginBottom: 8 }}>{error}</div>}
        {loading && <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>loading…</div>}

        {tab === 'friends' && (
          <>
            {friends.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-faint)' }}>
                no friends yet — head to <b>find</b> and search a handle
              </div>
            ) : (
              friends.map(f => (
                <div key={f.user_id} className="sk-box tight" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 6 }}>
                  <Link to={`/u/${f.handle.replace(/^@/, '')}`} style={{ flex: 1, color: 'inherit', textDecoration: 'none' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>@{f.handle.replace(/^@/, '')}</div>
                  </Link>
                  <button className="sk-btn ghost" style={{ fontSize: 11 }} onClick={() => remove(f.user_id)}>
                    unfriend
                  </button>
                </div>
              ))
            )}
          </>
        )}

        {tab === 'requests' && (
          <>
            <div className="sk-anno" style={{ color: 'var(--w-coral)', marginBottom: 6 }}>incoming</div>
            {incoming.length === 0
              ? <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginBottom: 14 }}>no incoming requests</div>
              : incoming.map(r => (
                  <div key={r.user_id} className="sk-box tight" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 6 }}>
                    <div style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>@{r.handle.replace(/^@/, '')}</div>
                    <button className="sk-btn primary" style={{ fontSize: 11 }} onClick={() => accept(r.user_id)}>accept</button>
                    <button className="sk-btn ghost" style={{ fontSize: 11 }} onClick={() => remove(r.user_id)}>decline</button>
                  </div>
                ))}

            <div className="sk-anno" style={{ color: 'var(--w-coral)', marginTop: 16, marginBottom: 6 }}>outgoing</div>
            {outgoing.length === 0
              ? <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>no outgoing requests</div>
              : outgoing.map(r => (
                  <div key={r.user_id} className="sk-box tight" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 6 }}>
                    <div style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>@{r.handle.replace(/^@/, '')}</div>
                    <span className="sk-pill" style={{ fontSize: 10 }}>pending</span>
                    <button className="sk-btn ghost" style={{ fontSize: 11 }} onClick={() => remove(r.user_id)}>cancel</button>
                  </div>
                ))}
          </>
        )}

        {tab === 'activity' && (
          <>
            {activityLoading && <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>loading…</div>}
            {!activityLoading && (activity ?? []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-faint)' }}>
                no recent friend solves — once your friends start solving problems, they'll show up here
              </div>
            )}
            {(activity ?? []).map((e, i) => {
              const cleanHandle = e.handle.replace(/^@/, '');
              const problemTitle = findProblemById(e.problem_id)?.title ?? e.problem_id;
              return (
                <div key={`${e.user_id}-${e.solved_at}-${i}`} className="sk-box tight" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 6 }}>
                  <Link to={`/u/${cleanHandle}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>@{cleanHandle}</span>
                  </Link>
                  <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>solved</span>
                  <Link to={`/problem/${e.problem_id}`} style={{ flex: 1, minWidth: 0, color: 'inherit', textDecoration: 'none' }}>
                    <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {problemTitle}
                    </span>
                  </Link>
                  <span className="sk-pill amber" style={{ fontSize: 10 }}>+{e.xp_earned} xp</span>
                  <span className="mono-cap" style={{ flexShrink: 0 }}>
                    {relativeTime(e.solved_at)}
                  </span>
                </div>
              );
            })}
          </>
        )}

        {tab === 'find' && (
          <div className="sk-box" style={{ padding: 14, maxWidth: 420 }}>
            <div className="sk-anno" style={{ color: 'var(--w-coral)', marginBottom: 8 }}>send a friend request</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                placeholder="@handle"
                value={findHandle}
                onChange={e => setFindHandle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && findHandle.trim()) void sendRequest(findHandle.trim().replace(/^@/, '')); }}
                style={{
                  flex: 1, padding: '6px 10px',
                  border: '1.4px solid var(--ink)', borderRadius: 8,
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
                  background: 'var(--paper)', color: 'var(--ink)',
                }}
              />
              <button
                className="sk-btn primary"
                disabled={finding || !findHandle.trim()}
                onClick={() => void sendRequest(findHandle.trim().replace(/^@/, ''))}
                style={{ fontSize: 11 }}
              >
                {finding ? '…' : 'send'}
              </button>
            </div>
            {findMsg && (
              <div style={{ marginTop: 8, fontSize: 11, color: findMsg.includes('failed') || findMsg.includes('no user') || findMsg.includes('already') ? 'var(--w-coral)' : 'var(--ink-soft)' }}>
                {findMsg}
              </div>
            )}
            <div className="mono-cap" style={{ marginTop: 12 }}>
              handles are case-insensitive · they can also send to you
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsPage;
